import { BORDER, RISK_LEVEL_COLORS, RISK_LEVELS } from "@app/theme/tokens";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { geoMercator, geoPath } from "d3-geo";
import type { FeatureCollection, Geometry } from "geojson";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { feature } from "topojson-client";
import type { Topology } from "topojson-specification";
import worldTopology from "world-atlas/countries-110m.json";
import type { FleetPlant } from "../types";
import { mw } from "../utils/format";
import { regionLabel } from "../utils/regions";
import DashboardCard from "./DashboardCard";
import { PendingCardBody, PendingValue } from "./PendingValue";

const INITIAL_WIDTH = 640;
/** The map drives the height of the whole charts row, so it stays fixed on wide screens. */
const HEIGHT = 320;
const PADDING = 24;
/** Degrees kept around the plants so a single-site fleet is not zoomed to street level. */
const MIN_SPAN = 6;
/** Room a region name takes under its pin, in viewBox units. */
const LABEL_WIDTH = 100;
const LABEL_HEIGHT = 13;
const LABEL_DROP = 26;

const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const BUTTON_STEP = 1.6;
/** Below this zoom the sites are too close to read, so they merge into their region. */
const CLUSTER_ZOOM = 2.5;

/** The register only carries a "Region" label, so the map is placed from a fixed gazetteer. */
const REGION_COORDINATES: Record<string, [number, number]> = {
  central: [46.6753, 24.7136],
  eastern: [50.0888, 26.4207],
  western: [39.1925, 21.4858],
  northern: [41.0231, 30.9843],
  southern: [42.5117, 18.2465],
  riyadh: [46.6753, 24.7136],
  makkah: [39.8579, 21.3891],
  madinah: [39.6142, 24.4686],
  qassim: [43.975, 26.326],
  asir: [42.5117, 18.2465],
  tabuk: [36.5662, 28.3835],
  hail: [41.6907, 27.5219],
  northernborders: [41.0231, 30.9843],
  jazan: [42.5511, 16.8892],
  najran: [44.1277, 17.4924],
  bahah: [41.4677, 20.0129],
  jawf: [40.1, 29.7856],
};

/** Registers name a site by its operating area, "EOA" for the Eastern one. */
const REGION_ALIASES: Record<string, string> = {
  eoa: "eastern",
  woa: "western",
  coa: "central",
  soa: "southern",
};

/** "Al-Jawf Region" and "AL JAWF" resolve to the same entry. */
function coordinatesOf(region: string | null): [number, number] | undefined {
  if (!region) return undefined;
  const key = region
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/region|province/g, "")
    .replace(/^al/, "");
  return REGION_COORDINATES[REGION_ALIASES[key] ?? key];
}

interface Placement {
  key: string;
  label: string;
  coordinates: [number, number];
}

interface MapPin extends Placement {
  plants: FleetPlant[];
  spofCount: number;
  worstRisk: string | null;
  exposedMw: number | null;
}

/** A plant with its own coordinates is pinned on its site; the others share their region pin. */
function placementOf(plant: FleetPlant): Placement | undefined {
  if (plant.latitude !== null && plant.longitude !== null) {
    return {
      key: `plant:${plant.id}`,
      label: plant.name,
      coordinates: [plant.longitude, plant.latitude],
    };
  }
  const regionCoordinates = coordinatesOf(plant.region);
  if (regionCoordinates && plant.region) {
    return {
      key: `region:${plant.region}`,
      label: regionLabel(plant.region),
      coordinates: regionCoordinates,
    };
  }
  return undefined;
}

function mapPins(plants: FleetPlant[], clustered: boolean): MapPin[] {
  const grouped = new Map<
    string,
    { label: string; sites: [number, number][]; plants: FleetPlant[] }
  >();
  for (const plant of plants) {
    const placement = placementOf(plant);
    if (!placement) continue;
    // Zoomed out, every site of a region answers to the same key so they merge into one pin.
    const key =
      clustered && plant.region ? `region:${plant.region}` : placement.key;
    const label =
      clustered && plant.region ? regionLabel(plant.region) : placement.label;
    const group = grouped.get(key) ?? { label, sites: [], plants: [] };
    group.sites.push(placement.coordinates);
    group.plants.push(plant);
    grouped.set(key, group);
  }
  return [...grouped].map(([key, group]) => {
    const exposures = group.plants
      .map((plant) => plant.exposed_mw)
      .filter((value) => value !== null);
    return {
      key,
      label: group.label,
      // A cluster sits at the centre of the sites it holds.
      coordinates: [
        mean(group.sites.map(([longitude]) => longitude)),
        mean(group.sites.map(([, latitude]) => latitude)),
      ],
      plants: group.plants,
      spofCount: group.plants.reduce(
        (total, plant) => total + (plant.spof_count ?? 0),
        0,
      ),
      worstRisk:
        RISK_LEVELS.find((level) =>
          group.plants.some((plant) => plant.worst_risk_level === level),
        ) ?? null,
      exposedMw: exposures.length ? Math.max(...exposures) : null,
    };
  });
}

function mean(values: number[]): number {
  return values.reduce((total, value) => total + value, 0) / values.length;
}

const topology = worldTopology as unknown as Topology;
const WORLD = feature(
  topology,
  topology.objects.countries,
) as unknown as FeatureCollection<Geometry>;

/** Bounding box of the pins, padded so the surrounding countries stay readable. */
function viewport(pins: MapPin[]) {
  const longitudes = pins.map((pin) => pin.coordinates[0]);
  const latitudes = pins.map((pin) => pin.coordinates[1]);
  const spanX = Math.max(...longitudes) - Math.min(...longitudes);
  const spanY = Math.max(...latitudes) - Math.min(...latitudes);
  const pad = Math.max(MIN_SPAN, spanX * 0.25, spanY * 0.25);
  // The corners are points on purpose: d3 reads a ring as a spherical polygon and would fit the whole globe.
  return {
    type: "MultiPoint",
    coordinates: [
      [Math.min(...longitudes) - pad, Math.min(...latitudes) - pad],
      [Math.max(...longitudes) + pad, Math.max(...latitudes) + pad],
    ],
  } as Geometry;
}

/** Pushes a name down until it clears the ones already written, so neighbouring plants stay readable. */
function labelRows(points: { x: number; y: number }[]) {
  const written: { x: number; y: number }[] = [];
  return points.map(({ x, y }) => {
    let labelY = y + LABEL_DROP;
    while (
      written.some(
        (other) =>
          Math.abs(other.x - x) < LABEL_WIDTH &&
          Math.abs(other.y - labelY) < LABEL_HEIGHT,
      )
    ) {
      labelY += LABEL_HEIGHT;
    }
    written.push({ x, y: labelY });
    return labelY;
  });
}

function PinTooltip({ pin }: { pin: MapPin }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="subtitle2">{pin.label}</Typography>
      <Typography variant="caption" component="div">
        {pin.plants.map((plant) => plant.name).join(", ")}
      </Typography>
      <Typography variant="caption" component="div">
        {pin.spofCount} SPOFs - worst risk {pin.worstRisk ?? "n/a"}
      </Typography>
      <Typography variant="caption" component="div">
        {pin.exposedMw === null ? (
          <PendingValue />
        ) : (
          `${mw(pin.exposedMw)} MW exposed`
        )}
      </Typography>
    </Box>
  );
}

function ZoomButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          size="small"
          aria-label={label}
          disabled={disabled}
          onClick={onClick}
          sx={{
            bgcolor: "background.paper",
            border: `1px solid ${BORDER}`,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function FleetMap({ plants }: { plants: FleetPlant[] }) {
  const navigate = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(INITIAL_WIDTH);
  const [view, setView] = useState({ k: MIN_ZOOM, x: 0, y: 0 });
  /** A pan must not open the plant it ends on. */
  const panned = useRef(false);
  const pointer = useRef<{ x: number; y: number } | null>(null);

  // The map is drawn at its real pixel size so it fills the card without stretching.
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(box.current!);
    return () => observer.disconnect();
  }, []);

  /** The fitted map always covers the frame: it can be panned only as far as its own edges. */
  const clamp = useCallback(
    (next: { k: number; x: number; y: number }) => ({
      k: next.k,
      x: Math.min(0, Math.max(width * (1 - next.k), next.x)),
      y: Math.min(0, Math.max(HEIGHT * (1 - next.k), next.y)),
    }),
    [width],
  );

  /** Zooms around a point of the frame, so what sits under the cursor stays under it. */
  const zoomAt = useCallback(
    (factor: number, focusX: number, focusY: number) =>
      setView((current) => {
        const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, current.k * factor));
        const ratio = k / current.k;
        return clamp({
          k,
          x: focusX - (focusX - current.x) * ratio,
          y: focusY - (focusY - current.y) * ratio,
        });
      }),
    [clamp],
  );

  const placed = plants.some((plant) => placementOf(plant));

  // React listens to the wheel passively, so zooming needs its own listener to hold the page still.
  useEffect(() => {
    const node = frame.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      zoomAt(
        Math.exp(-event.deltaY * 0.002),
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [zoomAt, placed]);

  const clustered = view.k < CLUSTER_ZOOM;
  const sitePins = mapPins(plants, false);
  const pins = clustered ? mapPins(plants, true) : sitePins;
  const unplaced = plants.filter((plant) => !placementOf(plant));

  const projection =
    sitePins.length === 0
      ? null
      : geoMercator().fitExtent(
          [
            [PADDING, PADDING],
            [width - PADDING, HEIGHT - PADDING],
          ],
          viewport(sitePins),
        );

  const path = projection && geoPath(projection);
  // The pins are placed by hand rather than inside the zoomed group, so they keep their size.
  const points = (projection ? pins : [])
    .map((pin) => {
      const [baseX, baseY] = projection!(pin.coordinates)!;
      return { pin, x: baseX * view.k + view.x, y: baseY * view.k + view.y };
    })
    .sort((left, right) => left.y - right.y);
  const labels = labelRows(points);

  return (
    <DashboardCard title="SPOF Risk by Region">
      <Box ref={box} sx={{ position: "relative" }}>
        {!path ? (
          <PendingCardBody hint="Set the plant coordinates in the expert view, or import a SPOF register carrying a known Region, to place the plants on the map." />
        ) : (
          <>
            <Box
              component="svg"
              ref={frame}
              viewBox={`0 0 ${width} ${HEIGHT}`}
              onPointerDown={(event: React.PointerEvent<SVGSVGElement>) => {
                panned.current = false;
                pointer.current = { x: event.clientX, y: event.clientY };
              }}
              onPointerMove={(event: React.PointerEvent<SVGSVGElement>) => {
                if (!pointer.current) return;
                const dx = event.clientX - pointer.current.x;
                const dy = event.clientY - pointer.current.y;
                if (Math.abs(dx) + Math.abs(dy) > 2 && !panned.current) {
                  panned.current = true;
                  // Captured only once the drag starts: a captured pointer would retarget
                  // the click to the frame and swallow the pin that opens the plant.
                  event.currentTarget.setPointerCapture(event.pointerId);
                }
                pointer.current = { x: event.clientX, y: event.clientY };
                setView((current) =>
                  clamp({ ...current, x: current.x + dx, y: current.y + dy }),
                );
              }}
              onPointerUp={() => {
                pointer.current = null;
              }}
              sx={{
                width: "100%",
                height: HEIGHT,
                display: "block",
                touchAction: "none",
                userSelect: "none",
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            >
              <rect width={width} height={HEIGHT} fill="#EAF1F7" rx={8} />
              <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
                {WORLD.features.map((country, index) => (
                  <path
                    key={index}
                    d={path(country) ?? undefined}
                    fill="#FFFFFF"
                    stroke={BORDER}
                    strokeWidth={0.6 / view.k}
                  />
                ))}
              </g>
              {points.map(({ pin, x, y }) => {
                const color =
                  RISK_LEVEL_COLORS[pin.worstRisk ?? ""] ?? "#8E99A8";
                // A pin holding a single plant drills down to it; a shared one zooms in until its sites split.
                const target = pin.plants.length === 1 ? pin.plants[0] : null;
                const radius = pin.plants.length > 1 ? 13 : 10;
                return (
                  <Tooltip key={pin.key} title={<PinTooltip pin={pin} />}>
                    <g
                      onClick={() => {
                        if (panned.current) return;
                        if (target) {
                          navigate(`/dashboard/plants/${target.id}`);
                        } else {
                          zoomAt(
                            Math.max(
                              BUTTON_STEP,
                              (CLUSTER_ZOOM * 1.1) / view.k,
                            ),
                            x,
                            y,
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <circle
                        cx={x}
                        cy={y}
                        r={radius + 4}
                        fill={color}
                        fillOpacity={0.25}
                      />
                      <circle
                        cx={x}
                        cy={y}
                        r={radius}
                        fill={color}
                        stroke="#FFFFFF"
                        strokeWidth={2}
                      />
                      <text
                        x={x}
                        y={y + 3.5}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={700}
                        fill="#FFFFFF"
                      >
                        {pin.spofCount}
                      </text>
                    </g>
                  </Tooltip>
                );
              })}
              {/* Names come after every pin so a neighbouring pin cannot cover them. */}
              {points.map(({ pin, x }, index) => (
                <text
                  key={pin.key}
                  x={x}
                  y={labels[index]}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#002A5C"
                  stroke="#EAF1F7"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {pin.label}
                </text>
              ))}
            </Box>

            <Stack
              spacing={0.5}
              sx={{ position: "absolute", top: 8, right: 8 }}
            >
              <ZoomButton
                label="Zoom in"
                disabled={view.k >= MAX_ZOOM}
                onClick={() => zoomAt(BUTTON_STEP, width / 2, HEIGHT / 2)}
              >
                <AddIcon fontSize="small" />
              </ZoomButton>
              <ZoomButton
                label="Zoom out"
                disabled={view.k <= MIN_ZOOM}
                onClick={() => zoomAt(1 / BUTTON_STEP, width / 2, HEIGHT / 2)}
              >
                <RemoveIcon fontSize="small" />
              </ZoomButton>
            </Stack>
          </>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}
      >
        {RISK_LEVELS.map((level) => (
          <Stack
            key={level}
            direction="row"
            spacing={0.5}
            sx={{ alignItems: "center" }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: RISK_LEVEL_COLORS[level],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {level}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {unplaced.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1, alignItems: "center" }}
        >
          <Typography variant="caption" color="text.secondary">
            Without a known location:
          </Typography>
          {unplaced.map((plant) => (
            <Chip
              key={plant.id}
              size="small"
              label={plant.name}
              variant="outlined"
            />
          ))}
        </Stack>
      )}
    </DashboardCard>
  );
}
